import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  Flex,
  DrawerOverlay,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  IconButton,
  ModalFooter,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useContext, useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { PrivateKey } from '@hashgraph/sdk';
import { GlobalContext } from 'providers/Global';
import { ModalType, SCREEN_MD } from '../../utils/constants';
import { getWalletErrorMessage } from '../../utils/walletErrors';
import {
  encryptToKeystore,
  decryptFromKeystore,
  changePassword,
  BadKeyError,
} from '../../signing/KeyManager';
import {
  saveKeystore,
  getKeystore,
  getKeystoreMeta,
  getConfig,
  saveKeystoreMeta,
  getAllKeystoreMetaForNetwork,
  deleteKeystore,
  deleteKeystoreMeta,
  KeystoreMeta,
} from '../../utils/networkConfigStore';
import { Buffer } from 'buffer';
import KeystoreExportButton from '../KeystoreExportButton';
import DropZone from '../DropZone';
import { createNetworkConfig, getNetworkConfigs } from '../../api/networkConfigAPI';

type AuthView = 'first-time' | 'returning' | 'account-select';

const HEDERA_ACCOUNT_ID_REGEX = /^\d+\.\d+\.\d+$/;

function isValidAccountId(id: string): boolean {
  return HEDERA_ACCOUNT_ID_REGEX.test(id.trim());
}

interface KeystoreAuthContentProps {
  networkId: string;
  networkName: string;
  onClose: () => void;
  onBackToNetworks?: () => void;
}

export const KeystoreAuthContent: React.FC<KeystoreAuthContentProps> = ({
  networkId,
  networkName,
  onClose,
  onBackToNetworks,
}) => {
  const {
    openModal,
    handleKeystoreUnlock,
    signInWithKey,
  } = useContext(GlobalContext);
  const toast = useToast();

  const [view, setView] = useState<AuthView>('first-time');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Delete account confirmation
  const [pendingDeleteAccountId, setPendingDeleteAccountId] = useState('');
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

  // First-time Path A state
  const [accountId, setAccountId] = useState('');
  const [rawKey, setRawKey] = useState('');
  const [selectedKeyType, setSelectedKeyType] = useState<'ED25519' | 'ECDSA'>('ED25519');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // First-time Path B state
  const [keystoreFile, setKeystoreFile] = useState<string | null>(null);
  const [keystoreFileName, setKeystoreFileName] = useState('');
  const [keystoreKeyType, setKeystoreKeyType] = useState<'ED25519' | 'ECDSA'>('ED25519');
  const [keystorePassword, setKeystorePassword] = useState('');
  const [keystoreAccountId, setKeystoreAccountId] = useState('');

  // Returning auth state
  const [returningAccountId, setReturningAccountId] = useState('');
  const [returningPassword, setReturningPassword] = useState('');

  // Account selector state
  const [savedAccounts, setSavedAccounts] = useState<KeystoreMeta[]>([]);

  // Warning modal
  const { isOpen: isWarningOpen, onOpen: onWarningOpen, onClose: onWarningClose } = useDisclosure();
  const [pendingBase64, setPendingBase64] = useState('');
  const [pendingAccountId, setPendingAccountId] = useState('');

  useEffect(() => {
    if (networkId) {
      const accounts = getAllKeystoreMetaForNetwork(networkId);
      setSavedAccounts(accounts);
      if (accounts.length > 1) {
        setView('account-select');
      } else if (accounts.length === 1) {
        setView('returning');
        setReturningAccountId(accounts[0].accountId);
      } else {
        setView('first-time');
      }
      setError('');
    }
  }, [networkId]);

  const handleClose = () => {
    onClose();
    resetState();
  };

  const resetState = () => {
    setAccountId('');
    setRawKey('');
    setSelectedKeyType('ED25519');
    setPassword('');
    setConfirmPassword('');
    setKeystoreFile(null);
    setKeystoreFileName('');
    setKeystoreKeyType('ED25519');
    setKeystorePassword('');
    setKeystoreAccountId('');
    setReturningPassword('');
    setError('');
    setIsLoading(false);
  };

  /**
   * Signs in to the backend with the key. False, with the reason shown, when
   * the backend refused or another sign-in overtook this one.
   */
  const signIn = async (signInAccountId: string, key: PrivateKey) => {
    try {
      return await signInWithKey(signInAccountId, networkId, key);
    } catch (err) {
      setError(getWalletErrorMessage(err, "Couldn't sign in. Check the network and try again"));
      return false;
    }
  };

  // Sync locally-stored config to backend after auth (so mirror proxy works)
  const syncConfigToBackend = async () => {
    try {
      // Check if config already exists on backend — avoids redundant
      // connectivity validation and duplicate name errors
      const existing = await getNetworkConfigs();
      const alreadySynced = existing?.data?.some(
        (c: any) => c.networkId === networkId,
      );
      if (alreadySynced) return;

      const config = getConfig(networkId);
      if (config) {
        await createNetworkConfig(config);
      }
    } catch (err) {
      // Non-fatal — backend may be unavailable
      console.warn('Config sync to backend:', (err as any)?.response?.data?.message || (err as any)?.message);
    }
  };


  const handlePathASubmit = async () => {
    setError('');

    if (!accountId.trim() || !isValidAccountId(accountId)) {
      setError('Valid Account ID required (format: 0.0.1234)');
      return;
    }
    if (!rawKey.trim()) {
      setError('Private key is required');
      return;
    }
    if (!password) {
      setError('Password is required');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);
    try {
      const trimmedKey = rawKey.trim().replace(/^0x/, '');
      const privateKey = selectedKeyType === 'ECDSA'
        ? PrivateKey.fromStringECDSA(trimmedKey)
        : PrivateKey.fromStringED25519(trimmedKey);
      setRawKey(''); // Clear raw key from React state immediately
      // Nothing is stored for a key the backend doesn't accept for this account
      if (!(await signIn(accountId.trim(), privateKey))) return;
      await syncConfigToBackend();
      const base64 = await encryptToKeystore(privateKey, password);

      saveKeystore(networkId, accountId.trim(), base64);
      saveKeystoreMeta(networkId, accountId.trim(), {
        accountId: accountId.trim(),
        publicKey: privateKey.publicKey.toStringDer(),
        keyType: selectedKeyType,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      });

      setPendingBase64(base64);
      setPendingAccountId(accountId.trim());
      onWarningOpen();
    } catch (err: any) {
      setError(err.message || 'Invalid private key format');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePathBSubmit = async () => {
    setError('');

    if (!keystoreAccountId.trim() || !isValidAccountId(keystoreAccountId)) {
      setError('Valid Account ID required (format: 0.0.1234)');
      return;
    }
    if (!keystoreFile) {
      setError('Keystore file is required');
      return;
    }
    if (!keystorePassword) {
      setError('Password is required');
      return;
    }

    setIsLoading(true);
    try {
      const key = await decryptFromKeystore(keystoreFile, keystorePassword, keystoreKeyType);
      if (!(await signIn(keystoreAccountId.trim(), key))) return;
      await syncConfigToBackend();

      saveKeystore(networkId, keystoreAccountId.trim(), keystoreFile);
      saveKeystoreMeta(networkId, keystoreAccountId.trim(), {
        accountId: keystoreAccountId.trim(),
        publicKey: key.publicKey.toStringDer(),
        keyType: keystoreKeyType,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      });

      handleKeystoreUnlock(networkId, keystoreAccountId.trim());

      toast({
        title: 'Keystore imported successfully',
        description: 'You can change your password from Settings.',
        status: 'success',
        duration: 4000,
      });
      handleClose();
    } catch (err: any) {
      if (err instanceof BadKeyError) {
        setError('Incorrect password for this keystore');
      } else {
        setError(err.message || 'Failed to decrypt keystore');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleReturningSubmit = async () => {
    setError('');

    if (!returningPassword) {
      setError('Password is required');
      return;
    }

    const base64 = getKeystore(networkId, returningAccountId);
    if (!base64) {
      setError('Keystore not found. Please re-authenticate.');
      setView('first-time');
      return;
    }

    setIsLoading(true);
    try {
      const meta = getKeystoreMeta(networkId, returningAccountId);
      const key = await decryptFromKeystore(base64, returningPassword, meta?.keyType);
      if (!(await signIn(returningAccountId, key))) return;
      await syncConfigToBackend();

      handleKeystoreUnlock(networkId, returningAccountId);
      toast({
        title: 'Authenticated successfully',
        status: 'success',
        duration: 3000,
      });
      handleClose();
    } catch (err: any) {
      if (err instanceof BadKeyError) {
        setError('Incorrect password');
      } else {
        setError(err.message || 'Decryption failed');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeystoreFileDrop = (file: File) => {
    setKeystoreFileName(file.name);

    // Try to extract account ID from filename (e.g., "keystore-0.0.1012.bin")
    const match = file.name.match(/(\d+\.\d+\.\d+)/);
    if (match && !keystoreAccountId) {
      setKeystoreAccountId(match[1]);
    }

    const reader = new FileReader();
    reader.onload = event => {
      const arrayBuffer = event.target?.result as ArrayBuffer;
      const base64 = Buffer.from(new Uint8Array(arrayBuffer)).toString('base64');
      setKeystoreFile(base64);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleDownloadBackup = () => {
    if (!pendingBase64 || !pendingAccountId) return;
    const bytes = Buffer.from(pendingBase64, 'base64');
    const blob = new Blob([bytes], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `keystore-${pendingAccountId}.bin`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleWarningAcknowledge = () => {
    onWarningClose();
    handleKeystoreUnlock(networkId, pendingAccountId);
    toast({
      title: 'Key encrypted and stored',
      status: 'success',
      duration: 3000,
    });
    handleClose();
  };

  const renderFirstTimeAuth = () => (
    <>
    {savedAccounts.length > 0 && (
      <Button
        variant="ghost"
        size="sm"
        mb={2}
        alignSelf="flex-start"
        onClick={() => {
          if (savedAccounts.length > 1) {
            setView('account-select');
          } else {
            setView('returning');
            setReturningAccountId(savedAccounts[0].accountId);
          }
          setError('');
        }}
      >
        ← Back
      </Button>
    )}
    <Tabs>
      <TabList>
        <Tab>Private Key</Tab>
        <Tab>Keystore File</Tab>
      </TabList>

      <TabPanels>
        <TabPanel>
          <VStack spacing={4} align="stretch">
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              Your key is encrypted locally and never transmitted
            </Alert>

            <FormControl isRequired>
              <FormLabel>Account ID</FormLabel>
              <Input
                placeholder="0.0.1234"
                value={accountId}
                onChange={e => setAccountId(e.target.value)}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Key Type</FormLabel>
              <Flex gap={2}>
                <Button
                  size="sm"
                  flex={1}
                  variant={selectedKeyType === 'ED25519' ? 'solid' : 'outline'}
                  colorScheme={selectedKeyType === 'ED25519' ? 'green' : 'gray'}
                  onClick={() => setSelectedKeyType('ED25519')}
                  borderRadius={6}
                >
                  ED25519
                </Button>
                <Button
                  size="sm"
                  flex={1}
                  variant={selectedKeyType === 'ECDSA' ? 'solid' : 'outline'}
                  colorScheme={selectedKeyType === 'ECDSA' ? 'green' : 'gray'}
                  onClick={() => setSelectedKeyType('ECDSA')}
                  borderRadius={6}
                >
                  ECDSA
                </Button>
              </Flex>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Private Key</FormLabel>
              <Input
                type="password"
                placeholder="Enter your private key"
                value={rawKey}
                onChange={e => setRawKey(e.target.value)}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Password</FormLabel>
              <Input
                type="password"
                placeholder="Create a password"
                value={password}
                onChange={e => setPassword(e.target.value)}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Confirm Password</FormLabel>
              <Input
                type="password"
                placeholder="Confirm password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
              />
            </FormControl>

            {error && (
              <Text color="red.500" fontSize="sm">
                {error}
              </Text>
            )}

            <Button
              colorScheme="blue"
              onClick={handlePathASubmit}
              isLoading={isLoading}
              loadingText="Encrypting key..."
            >
              Encrypt & Store Key
            </Button>
          </VStack>
        </TabPanel>

        <TabPanel>
          <VStack spacing={4} align="stretch">
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              Your key is encrypted locally and never transmitted
            </Alert>

            <FormControl isRequired>
              <FormLabel>Account ID</FormLabel>
              <Input
                placeholder="0.0.1234"
                value={keystoreAccountId}
                onChange={e => setKeystoreAccountId(e.target.value)}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Key Type</FormLabel>
              <Flex gap={2}>
                <Button
                  size="sm"
                  flex={1}
                  variant={keystoreKeyType === 'ED25519' ? 'solid' : 'outline'}
                  colorScheme={keystoreKeyType === 'ED25519' ? 'green' : 'gray'}
                  onClick={() => setKeystoreKeyType('ED25519')}
                  borderRadius={6}
                >
                  ED25519
                </Button>
                <Button
                  size="sm"
                  flex={1}
                  variant={keystoreKeyType === 'ECDSA' ? 'solid' : 'outline'}
                  colorScheme={keystoreKeyType === 'ECDSA' ? 'green' : 'gray'}
                  onClick={() => setKeystoreKeyType('ECDSA')}
                  borderRadius={6}
                >
                  ECDSA
                </Button>
              </Flex>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Keystore File</FormLabel>
              <DropZone
                accept=".bin,.keystore"
                label="Drop keystore file here, or click to browse"
                sublabel="Accepts .bin or .keystore files"
                onFile={handleKeystoreFileDrop}
                fileName={keystoreFileName}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Password</FormLabel>
              <Input
                type="password"
                placeholder="Enter keystore password"
                value={keystorePassword}
                onChange={e => setKeystorePassword(e.target.value)}
              />
            </FormControl>

            {error && (
              <Text color="red.500" fontSize="sm">
                {error}
              </Text>
            )}

            <Button
              colorScheme="blue"
              onClick={handlePathBSubmit}
              isLoading={isLoading}
              loadingText="Decrypting keystore..."
            >
              Import Keystore
            </Button>
          </VStack>
        </TabPanel>
      </TabPanels>
    </Tabs>
    </>
  );

  const renderReturningAuth = () => (
    <VStack spacing={4} align="stretch">
      <Button
        variant="ghost"
        size="sm"
        alignSelf="flex-start"
        onClick={() => {
          if (savedAccounts.length > 1) {
            setView('account-select');
          } else if (onBackToNetworks) {
            onBackToNetworks();
          } else {
            handleClose();
            openModal(ModalType.PrivateNetworksDrawer);
          }
          setReturningPassword('');
          setError('');
        }}
      >
        {savedAccounts.length > 1 ? '← Switch Account' : '← Back to Networks'}
      </Button>

      <FormControl>
        <FormLabel>Account ID</FormLabel>
        <Input value={returningAccountId} isReadOnly bg="gray.50" />
      </FormControl>

      <FormControl isRequired>
        <FormLabel>Password</FormLabel>
        <Input
          type="password"
          placeholder="Enter your password"
          value={returningPassword}
          onChange={e => setReturningPassword(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleReturningSubmit()}
        />
      </FormControl>

      {error && (
        <Text color="red.500" fontSize="sm">
          {error}
        </Text>
      )}

      <Button
        colorScheme="blue"
        onClick={handleReturningSubmit}
        isLoading={isLoading}
        loadingText="Decrypting..."
      >
        Unlock
      </Button>

      <Button variant="link" size="sm" onClick={() => setView('first-time')}>
        Use a different key
      </Button>

      <Divider />

      <Flex gap={2} w="full">
        <KeystoreExportButton networkId={networkId} accountId={returningAccountId} />
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            openModal(ModalType.ChangePasswordModal, {
              networkId,
              accountId: returningAccountId,
            });
          }}
        >
          Change Password
        </Button>
      </Flex>
    </VStack>
  );

  const handleSelectAccount = (acctId: string) => {
    setReturningAccountId(acctId);
    setReturningPassword('');
    setError('');
    setView('returning');
  };

  const handleDeleteAccount = (acctId: string) => {
    setPendingDeleteAccountId(acctId);
    onDeleteOpen();
  };

  const confirmDeleteAccount = () => {
    deleteKeystore(networkId, pendingDeleteAccountId);
    deleteKeystoreMeta(networkId, pendingDeleteAccountId);
    const updated = getAllKeystoreMetaForNetwork(networkId);
    setSavedAccounts(updated);
    onDeleteClose();
    setPendingDeleteAccountId('');
    toast({
      title: `Account ${pendingDeleteAccountId} removed`,
      status: 'info',
      duration: 3000,
    });
    // Navigate based on remaining accounts
    if (updated.length === 0) {
      setView('first-time');
    } else if (updated.length === 1) {
      setReturningAccountId(updated[0].accountId);
      setView('returning');
    }
  };

  const renderAccountSelect = () => (
    <VStack spacing={4} align="stretch">
      <Button
        variant="ghost"
        size="sm"
        alignSelf="flex-start"
        onClick={() => {
          if (onBackToNetworks) {
            onBackToNetworks();
          } else {
            handleClose();
            openModal(ModalType.PrivateNetworksDrawer);
          }
        }}
      >
        ← Back to Networks
      </Button>

      <Text fontSize="sm" color="blackAlpha.600">
        Select an account to unlock, or add a new one.
      </Text>

      {savedAccounts.map((meta) => (
        <Flex
          key={meta.accountId}
          p={4}
          bg="primary"
          borderRadius={8}
          alignItems="center"
          gap={3}
          transition="all 0.15s"
          _hover={{ bg: 'blackAlpha.100' }}
        >
          <Box
            as="button"
            flex={1}
            textAlign="left"
            onClick={() => handleSelectAccount(meta.accountId)}
          >
            <Text variant="bodyBold" size="sm">{meta.accountId}</Text>
            <Text fontSize="xs" color="blackAlpha.500">
              {meta.keyType} · Added {new Date(meta.createdAt).toLocaleDateString()}
            </Text>
          </Box>
          <IconButton
            aria-label="Remove account"
            icon={<Text fontSize="lg">×</Text>}
            size="xs"
            variant="ghost"
            color="red.400"
            _hover={{ bg: 'red.50', color: 'red.600' }}
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteAccount(meta.accountId);
            }}
          />
        </Flex>
      ))}

      <Divider />

      <Button
        variant="outline"
        size="sm"
        onClick={() => {
          setView('first-time');
          setError('');
        }}
      >
        + Add Another Account
      </Button>
    </VStack>
  );

  return (
    <>
      <VStack spacing={0} align="stretch">
        {view === 'first-time' && renderFirstTimeAuth()}
        {view === 'returning' && renderReturningAuth()}
        {view === 'account-select' && renderAccountSelect()}
      </VStack>

      <Modal
        isOpen={isWarningOpen}
        onClose={() => {}}
        closeOnOverlayClick={false}
        closeOnEsc={false}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Remember Your Password</ModalHeader>
          <ModalBody pb={6}>
            <VStack spacing={4}>
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                <Box>
                  <Text fontWeight="bold">
                    Store your password in a safe place.
                  </Text>
                  <Text fontSize="sm" mt={1}>
                    If you forget your password, you will lose access to your encrypted key. There is no way to recover it without the password.
                  </Text>
                </Box>
              </Alert>

              <Box w="full" bg="gray.50" p={3} borderRadius="md">
                <Text fontSize="sm" color="gray.600">
                  You can download a keystore backup file for convenience — it lets you quickly import your account on another device or browser. The backup is encrypted with the same password.
                </Text>
              </Box>

              <Button variant="outline" w="full" onClick={handleDownloadBackup}>
                Download Keystore Backup
              </Button>

              <Button variant="primary" w="full" onClick={handleWarningAcknowledge}>
                I Understand
              </Button>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Remove Account</ModalHeader>
          <ModalBody>
            <Text>
              Are you sure you want to remove <strong>{pendingDeleteAccountId}</strong>?
            </Text>
            <Text fontSize="sm" color="red.500" mt={2}>
              You will need to re-import the private key or keystore file to use this account again.
            </Text>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="outline" onClick={onDeleteClose}>Cancel</Button>
            <Button colorScheme="red" onClick={confirmDeleteAccount}>Remove</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

const KeystoreAuthDrawer = () => {
  const {
    modals: { [`${ModalType.KeystoreAuthDrawer}`]: modalState },
    closeModal,
  } = useContext(GlobalContext);

  const open = modalState?.open ?? false;
  const networkId = (modalState as any)?.networkId ?? '';
  const networkName = (modalState as any)?.networkName ?? '';
  const isMobile = useMediaQuery({ query: `(max-width: ${SCREEN_MD})` });

  const handleClose = () => closeModal(ModalType.KeystoreAuthDrawer);

  if (!open) return null;

  return (
    <Drawer isOpen={open} placement="right" onClose={handleClose} size={isMobile ? 'full' : 'md'}>
      <DrawerOverlay backdropFilter="blur(10px)" />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>{networkName || 'Custom Network'}</DrawerHeader>
        <DrawerBody>
          <KeystoreAuthContent
            networkId={networkId}
            networkName={networkName}
            onClose={handleClose}
          />
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default KeystoreAuthDrawer;
